/**
 * A tabela do miolo de Finanças: contorno, cabeçalho, linhas, células e a linha
 * de vazio.
 *
 * Papel: reproduz `.tbl` e o que vem com ela (l. 84–93, 201) — Outfit 12 px em
 * `--emb-texto-tabela`, cabeçalho em Outfit 11 px 300 caixa alta com borda de
 * baixo de 2 px e **fundo branco** (a regra de l. 201 vem depois da de l. 85 e
 * vence), célula `9px 12px`, linha com filete embaixo e `--background` sob o
 * ponteiro —, e a linha única que diz "Carregando…" ou "Não foi possível
 * carregar." no lugar das linhas, como a base pede.
 *
 * Como usar: `Celula` com `tipo="numero"` é o `.r` (à direita, números
 * tabulares, sem quebra) e com `tipo="fraca"` é o `.mu`; o resto do desenho de
 * cada coluna — fonte, peso, cor — vai por `className`, como a origem põe por
 * `style`. `LinhaVazia` recebe o texto do vazio de cada tabela e o respiro dela
 * por `className`: a de lançamentos tem 16 px, a do Fechamento 12 px e 12 px de
 * fonte.
 */
import { type ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';
import { Botao } from './botao';

/**
 * O contorno da tabela.
 *
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns A `<table>`.
 */
export function Tabela({ className, ...props }: ComponentProps<'table'>) {
  return (
    <table
      className={cn(
        'w-full border-collapse font-outfit text-[12px] text-emb-texto-tabela',
        className,
      )}
      {...props}
    />
  );
}

interface CelulaDeCabecalhoProps extends ComponentProps<'th'> {
  /** `direita` é o `th.r` da coluna Valor. */
  alinhamento?: 'esquerda' | 'direita';
}

/**
 * Uma célula do cabeçalho.
 *
 * @param alinhamento - `direita` alinha o texto à direita.
 * @param className - Classes de quem chama, como a largura da coluna.
 * @returns O `<th>`.
 */
export function CelulaDeCabecalho({
  alinhamento = 'esquerda',
  className,
  ...props
}: CelulaDeCabecalhoProps) {
  return (
    <th
      scope="col"
      className={cn(
        'border-b-2 border-emb-borda bg-superficie px-3 py-3 font-outfit text-[11px] font-light tracking-[0.6px] whitespace-nowrap text-foreground uppercase',
        alinhamento === 'direita' ? 'text-right' : 'text-left',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Uma linha do corpo, com o filete embaixo e o fundo sob o ponteiro.
 *
 * @param className - Classes de quem chama, como a opacidade do arraste.
 * @returns O `<tr>`.
 */
export function LinhaDaTabela({ className, ...props }: ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'border-b border-emb-borda transition-all duration-100 last:border-b-0 hover:bg-background',
        className,
      )}
      {...props}
    />
  );
}

/** Os três jeitos de célula da origem: comum, `.r` e `.mu`. */
export type TipoDeCelula = 'comum' | 'numero' | 'fraca';

/** O que cada tipo acrescenta à célula comum. */
const CLASSES_DO_TIPO: Readonly<Record<TipoDeCelula, string>> = {
  comum: '',
  numero: 'text-right whitespace-nowrap tabular-nums',
  fraca: 'font-outfit text-emb-texto-tabela text-[12px] font-light',
};

/**
 * Uma célula do corpo.
 *
 * @param tipo - `numero` para o `.r`, `fraca` para o `.mu`.
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O `<td>`.
 */
export function Celula({
  tipo = 'comum',
  className,
  ...props
}: ComponentProps<'td'> & { tipo?: TipoDeCelula }) {
  return (
    <td
      className={cn('px-3 py-[9px] align-middle', CLASSES_DO_TIPO[tipo], className)}
      {...props}
    />
  );
}

/** O que a linha de vazio diz. */
export type SituacaoDaLinhaVazia = 'vazia' | 'carregando' | 'falhou';

interface LinhaVaziaProps {
  /** Quantas colunas a tabela tem, para o `colSpan`. */
  colunas: number;
  situacao: SituacaoDaLinhaVazia;
  /** O texto da origem para a tabela sem linha, como "Nenhum lançamento encontrado.". */
  textoDoVazio: string;
  /** Chamado pelo "Tentar de novo" da falha. */
  onTentarDeNovo: () => void;
  /** O respiro e a fonte daquela tabela. */
  className?: string;
}

/**
 * A linha única que ocupa a tabela quando não há linha para mostrar.
 *
 * @param colunas - O `colSpan` da célula.
 * @param situacao - `vazia` diz `textoDoVazio`, `carregando` diz "Carregando…"
 *   e `falhou` diz "Não foi possível carregar." com o botão.
 * @param textoDoVazio - O texto da tabela sem linha.
 * @param onTentarDeNovo - O manipulador do botão da falha.
 * @param className - Classes de quem chama, aplicadas por último.
 * @returns O `<tr>`.
 */
export function LinhaVazia({
  colunas,
  situacao,
  textoDoVazio,
  onTentarDeNovo,
  className,
}: LinhaVaziaProps) {
  return (
    <tr>
      <td colSpan={colunas} className={cn('p-4 text-center text-texto-fraco', className)}>
        {situacao === 'vazia' && textoDoVazio}
        {situacao === 'carregando' && 'Carregando…'}
        {situacao === 'falhou' && (
          <>
            Não foi possível carregar.{' '}
            <Botao variante="fantasma" tamanho="sm" onClick={onTentarDeNovo}>
              Tentar de novo
            </Botao>
          </>
        )}
      </td>
    </tr>
  );
}
